import { useEffect, useState } from 'react';
import OverlayModal from './OverlayModal';

type Props = {
  isOpen: boolean;
  title: string;
  /** When provided, modal shows this blob URL (caller owns revoke). */
  objectUrl: string | null;
  loading?: boolean;
  error?: string | null;
  onClose: () => void;
};

/** In-app PDF viewer for an already-fetched (or loading) blob URL. */
export default function BlobPdfPreviewModal({
  isOpen,
  title,
  objectUrl,
  loading = false,
  error = null,
  onClose,
}: Props) {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const [frameError, setFrameError] = useState(false);
  useEffect(() => {
    setFrameError(false);
  }, [objectUrl, isOpen]);

  return (
    <OverlayModal
      isOpen={isOpen}
      overlayClassName="overlay doc-preview-overlay"
      contentClassName="doc-preview-mbox"
      centerVertically
      onClose={onClose}
    >
      <div className="doc-preview-hdr">
        <div className="doc-preview-hdr-text">
          <div className="doc-preview-title">{title || 'Document preview'}</div>
          <div className="doc-preview-sub">PDF</div>
        </div>
        <button type="button" className="cn-x doc-preview-close" aria-label="Close preview" onClick={onClose}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <div className="doc-preview-body">
        {loading ? (
          <p className="doc-preview-msg">Loading preview…</p>
        ) : error ? (
          <p className="doc-preview-msg doc-preview-msg--err">{error}</p>
        ) : objectUrl && !frameError ? (
          <iframe
            title={title}
            className="doc-preview-frame"
            src={objectUrl}
            onError={() => setFrameError(true)}
          />
        ) : (
          <p className="doc-preview-msg">No preview available.</p>
        )}
      </div>
    </OverlayModal>
  );
}
